import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FAMILY_ROLE_LABELS, FamilyRole } from '@/types/invitation';
import {
  useInvitationFamily,
  useAddFamilyMember,
  useDeleteFamilyMember,
} from '@/hooks/useInvitations';

interface Props {
  invitationId: string | undefined;
}

const FamilyTab = ({ invitationId }: Props) => {
  const isNew = !invitationId;
  const { data: familyMembers } = useInvitationFamily(invitationId || '');
  const addFamilyMember = useAddFamilyMember();
  const deleteFamilyMember = useDeleteFamilyMember();

  const [name, setName] = useState('');
  const [role, setRole] = useState<FamilyRole>('bride_father');

  const handleAdd = async () => {
    if (!name || !invitationId) return;
    await addFamilyMember.mutateAsync({
      invitation_id: invitationId,
      name,
      role,
    });
    setName('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Padres y Padrinos</CardTitle>
        <CardDescription>
          Información de los familiares que participan en la boda
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isNew ? (
          <p className="text-muted-foreground text-center py-4">
            Guarda la invitación primero para agregar familiares
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 items-end">
              <div className="space-y-2 flex-1">
                <Label>Nombre</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nombre completo"
                />
              </div>
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select value={role} onValueChange={(v) => setRole(v as FamilyRole)}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(FAMILY_ROLE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                onClick={handleAdd}
                disabled={addFamilyMember.isPending}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {familyMembers && familyMembers.length > 0 && (
              <div className="space-y-2 mt-4">
                {familyMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-4 p-3 bg-muted rounded-lg"
                  >
                    <div className="flex-1">
                      <p className="font-medium">{member.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {FAMILY_ROLE_LABELS[member.role as FamilyRole]}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      onClick={() =>
                        deleteFamilyMember.mutate({
                          id: member.id,
                          invitationId,
                        })
                      }
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FamilyTab;
